import { API_BASE_URL } from "../apiConfig";
import React, { useState, useEffect, useCallback } from "react";
import "./ComplaintControl.css";
import ObjectDetectionViewer from "./ObjectDetectionViewer";

const ComplaintControl = () => {
  const [complaints, setComplaints] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  
  // Modals state
  const [showSeverityModal, setShowSeverityModal] = useState(false);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedComplaints, setSelectedComplaints] = useState([]);
  const [newSeverity, setNewSeverity] = useState("");
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const [previewDetection, setPreviewDetection] = useState(null);
  const [toolbarToast, setToolbarToast] = useState(null);
  const hasNoSeverityComplaint = selectedComplaints.some(
    (complaint) => complaint.severity === "No Severity Detected"
  );

  const showToolbarError = (msg) => {
    setToolbarToast(msg);
    setTimeout(() => setToolbarToast(null), 3000);
  };
  const fetchComplaints = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/complaint/all-control?page=${page}&limit=5`);
      if (!res.ok) throw new Error("Failed to fetch");
      const data = await res.json();
      setComplaints(data.complaints);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) {
      console.error("Error fetching complaints:", err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  const fetchDrivers = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/driver/all`);
      if (!res.ok) throw new Error("Failed to fetch drivers");
      const data = await res.json();
      console.log("Fetched drivers:", data);
      setDrivers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching drivers:", err);
    }
  }, []);

  useEffect(() => {
    setSelectedComplaints([]);
    fetchComplaints();
  }, [fetchComplaints]);

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  const handleOverrideSeverity = async () => {
    console.log("🛠 [ADMIN] Overriding to:", newSeverity);
    if (!newSeverity || selectedComplaints.length === 0) return;
    try {
      await Promise.all(selectedComplaints.map(async (c) => {
        const res = await fetch(`${API_BASE_URL}/api/complaint/override-severity/${c._id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ severity: newSeverity })
        });
        if (!res.ok) throw new Error("Update failed");
      }));
      console.log("✅ [ADMIN] Override successful");
      setShowSeverityModal(false);
      setSelectedComplaints([]);
      fetchComplaints();
    } catch (err) {
      console.error("❌ [ADMIN] Override error:", err);
      alert("Failed to override severity");
    }
  };

  const handleAssignDriver = async () => {
    if (!selectedDriverId || selectedComplaints.length === 0) return;
    try {
      await Promise.all(selectedComplaints.map(async (c) => {
        const res = await fetch(`${API_BASE_URL}/api/complaint/assign-driver/${c._id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ driverId: selectedDriverId })
        });
        if (!res.ok) throw new Error("Assignment failed");
      }));
      setShowDriverModal(false);
      setSelectedComplaints([]);
      fetchComplaints();
    } catch (err) {
      alert("Failed to assign driver");
    }
  };

  const handleDeleteComplaint = async () => {
    if (selectedComplaints.length === 0) return;
    try {
      await Promise.all(selectedComplaints.map(async (c) => {
        const res = await fetch(`${API_BASE_URL}/api/complaint/delete/${c._id}`, {
          method: "DELETE"
        });
        if (!res.ok) throw new Error("Delete failed");
      }));
      setShowDeleteModal(false);
      setSelectedComplaints([]);
      fetchComplaints();
    } catch (err) {
      alert("Failed to delete complaint");
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="complaint-control">
      <div className="control-header">
        <div className="header-text">
          <h1>Complaint Control</h1>
          <p>View all complaints and take necessary actions.</p>
        </div>
        <div className="header-stats">
          <div className="stat-item">
            <span className="stat-label">Total Complaints:</span>
            <span className="stat-value">{total}</span>
          </div>
        </div>
      </div>

      {/* Error Toast */}
      {toolbarToast && (
        <div className="toolbar-toast">{toolbarToast}</div>
      )}

      {/* Actions Toolbar */}
      <div className="control-actions-toolbar">

        {/* Override Severity */}
        <button
          className="toolbar-btn override-btn"
          disabled={
            selectedComplaints.length === 0 ||
            selectedComplaints.some(c => c.status === "completed")
          }
          onClick={() => {
            if (selectedComplaints.length === 0) return;
            const validOptions = ["High", "Medium", "Low"];
            const firstComp = selectedComplaints[0];
            setNewSeverity(validOptions.includes(firstComp.severity) ? firstComp.severity : "High");
            setShowSeverityModal(true);
          }}
          title={selectedComplaints.some(c => c.status === "completed") ? "Cannot override completed complaint" : "Override AI Severity"}
        >
          🛡️ Override Severity
        </button>

        {/* Assign Driver */}
        <button
          className="toolbar-btn assign-btn"
          disabled={
            selectedComplaints.length === 0 ||
            selectedComplaints.some(c => c.status === "completed") ||
            hasNoSeverityComplaint ||
            selectedComplaints.every(c => c.assignedDriver)
          }
          onClick={() => {
            if (selectedComplaints.length === 0) return;
            if (selectedComplaints.every(c => c.assignedDriver)) {
              showToolbarError("❌ All selected complaints already have a driver. Use Reassign Driver.");
              return;
            }
            setSelectedDriverId("");
            fetchDrivers();
            setShowDriverModal(true);
          }}
          title={
            selectedComplaints.some(c => c.status === "completed")
              ? "Cannot assign driver to completed complaint"
              : hasNoSeverityComplaint
              ? "Complaints with no severity detected cannot be assigned to drivers"
              : "Assign Driver (only for unassigned complaints)"
          }
        >
          👤 Assign Driver
        </button>

        {/* Reassign Driver */}
        <button
          className="toolbar-btn reassign-btn"
          disabled={
            selectedComplaints.length === 0 ||
            selectedComplaints.some(c => c.status === "completed") ||
            hasNoSeverityComplaint ||
            selectedComplaints.every(c => !c.assignedDriver)
          }
          onClick={() => {
            if (selectedComplaints.length === 0) return;
            if (selectedComplaints.every(c => !c.assignedDriver)) {
              showToolbarError("❌ No driver assigned yet. Use Assign Driver first.");
              return;
            }
            const firstComp = selectedComplaints[0];
            setSelectedDriverId(firstComp.assignedDriver?._id || "");
            fetchDrivers();
            setShowDriverModal(true);
          }}
          title={
            selectedComplaints.some(c => c.status === "completed")
              ? "Cannot reassign driver to completed complaint"
              : hasNoSeverityComplaint
              ? "Complaints with no severity detected cannot be assigned to drivers"
              : "Reassign Driver (only for already assigned complaints)"
          }
        >
          🔄 Reassign Driver
        </button>

        {/* Delete */}
        <button
          className="toolbar-btn delete-btn"
          disabled={selectedComplaints.length === 0}
          onClick={() => {
            if (selectedComplaints.length === 0) return;
            setShowDeleteModal(true);
          }}
          title="Delete selected complaints"
        >
          🗑️ Delete
        </button>

        {selectedComplaints.length > 0 && (
          <span className="selected-info">
            Selected: <strong>{selectedComplaints.length} complaint{selectedComplaints.length > 1 ? "s" : ""}</strong>
          </span>
        )}
      </div>

      <div className="control-table-container">
        <table className="control-table">
          <thead>
            <tr>
              <th style={{ width: "60px", textAlign: "center" }}>
                <input 
                  type="checkbox"
                  checked={complaints.length > 0 && selectedComplaints.length === complaints.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedComplaints(complaints);
                    } else {
                      setSelectedComplaints([]);
                    }
                  }}
                  className="select-checkbox"
                />
              </th>
              <th>Complaint ID</th>
              <th>User</th>
              <th>Complaint</th>
              <th style={{ textAlign: "center" }}>Detections</th>
              <th style={{ textAlign: "center" }}>Proof Image</th>
              <th>AI Severity</th>
              <th>Assigned Driver</th>
              <th>Status</th>
              <th>Submitted On</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="10" style={{textAlign: "center", padding: "40px"}}>Loading...</td></tr>
            ) : complaints.length === 0 ? (
              <tr><td colSpan="10" style={{textAlign: "center", padding: "40px"}}>No complaints found.</td></tr>
            ) : (
              complaints.map((c) => {
                const isSelected = selectedComplaints.some(sc => sc._id === c._id);
                return (
                  <tr 
                    key={c._id}
                    onClick={(e) => {
                      // Don't change selection if clicking on the image thumb, checkbox or button
                      if (e.target.tagName === 'IMG' || e.target.type === 'checkbox' || e.target.tagName === 'BUTTON') return;
                      if (isSelected) {
                        setSelectedComplaints(selectedComplaints.filter(sc => sc._id !== c._id));
                      } else {
                        setSelectedComplaints([...selectedComplaints, c]);
                      }
                    }}
                    className={`table-row ${isSelected ? "selected" : ""}`}
                    style={{ cursor: "pointer" }}
                  >
                    <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                      <input 
                        type="checkbox" 
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedComplaints([...selectedComplaints, c]);
                          } else {
                            setSelectedComplaints(selectedComplaints.filter(sc => sc._id !== c._id));
                          }
                        }}
                        className="select-checkbox"
                      />
                    </td>
                    <td className="complaint-id">CMP-{c._id.substring(c._id.length - 6).toUpperCase()}</td>
                    <td>{c.username}</td>
                    <td className="complaint-cell">
                      <div className="complaint-content">
                        {c.description && <p className="complaint-desc">{c.description}</p>}
                        {c.image && (
                          <img 
                            src={`${API_BASE_URL}/uploads/${c.image}`} 
                            alt="complaint" 
                            className="complaint-thumb"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewImage(`${API_BASE_URL}/uploads/${c.image}`);
                            }}
                          />
                        )}
                        {!c.description && !c.image && <span>No description or image</span>}
                      </div>
                    </td>
                    <td className="detection-cell" style={{ textAlign: "center" }}>
                      {c.image && c.detectedItems && c.detectedItems.length > 0 ? (
                        <button 
                          className="toolbar-btn view-btn"
                          style={{ padding: "6px 12px", width: "auto", margin: "0 auto" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewDetection(`${API_BASE_URL}/uploads/${c.image}`);
                          }}
                        >
                          👁️ View
                        </button>
                      ) : (
                        <span style={{color: "#94a3b8", fontSize: "12px"}}>None</span>
                      )}
                    </td>
                    <td className="proof-cell" style={{ textAlign: "center" }}>
                      {c.proofImage ? (
                        <img 
                          src={`${API_BASE_URL}/uploads/proofs/${c.proofImage}`} 
                          alt="proof" 
                          className="complaint-thumb"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewImage(`${API_BASE_URL}/uploads/proofs/${c.proofImage}`);
                          }}
                        />
                      ) : (
                        <span style={{color: "#94a3b8", fontSize: "12px"}}>No Proof</span>
                      )}
                    </td>
                    <td>
                      <span className={`severity-badge severity-${(c.aiSeverity || "pending").split(' ')[0].toLowerCase()}`}>
                        {c.aiSeverity || "Pending AI Analysis"}
                      </span>
                      {c.isSeverityOverridden && (
                        <span style={{display: "block", fontSize: "11px", color: "#ef2929", marginTop: "6px", fontWeight: "700"}}>
                          ⚠️ Overridden to {c.severity}
                        </span>
                      )}
                    </td>
                    <td>{c.assignedDriver ? c.assignedDriver.username : "—"}</td>
                    <td>
                      <span className={`status-pill ${c.status?.toLowerCase()}`}>
                        {c.status?.toUpperCase() || "PENDING"}
                      </span>
                    </td>
                    <td>{formatDate(c.createdAt)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        <div className="pagination">
          <button 
            className="page-btn" 
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            &lt;
          </button>
          <span className="page-info">Page {page} of {pages}</span>
          <button 
            className="page-btn" 
            disabled={page === pages}
            onClick={() => setPage(page + 1)}
          >
            &gt;
          </button>
        </div>
      </div>

      {/* Severity Modal */}
      {showSeverityModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Override Severity</h2>
            <div className="modal-form">
              <label>Select New Severity</label>
              <select value={newSeverity} onChange={(e) => setNewSeverity(e.target.value)}>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowSeverityModal(false)}>Cancel</button>
              <button className="save-btn" onClick={handleOverrideSeverity}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Driver Modal */}
      {showDriverModal && selectedComplaints.length > 0 && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>{selectedComplaints.some(c => c.assignedDriver) ? "Reassign Driver" : "Assign Driver"}</h2>
            <div className="modal-form">
              <label>Select Driver</label>
              <select value={selectedDriverId} onChange={(e) => setSelectedDriverId(e.target.value)}>
                <option value="">-- Choose Driver --</option>
                {drivers.length > 0 ? (
                  drivers.map(d => (
                    <option key={d._id} value={d._id}>{d.username}</option>
                  ))
                ) : (
                  <option disabled>No drivers found. Register a driver first.</option>
                )}
              </select>
              <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "8px"}}>
                {drivers.length === 0 && <p style={{fontSize: "11px", color: "#ef2929", margin: 0}}>No registered drivers detected.</p>}
                <button 
                  type="button"
                  onClick={fetchDrivers} 
                  style={{fontSize: "10px", padding: "2px 8px", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: "4px", cursor: "pointer"}}
                >
                  🔄 Refresh List
                </button>
              </div>
            </div>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowDriverModal(false)}>Cancel</button>
              <button className="save-btn" onClick={handleAssignDriver}>Confirm Assignment</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content delete-modal">
            <div className="delete-icon-wrapper">🗑️</div>
            <h2>Confirm Deletion</h2>
            <p>
              Are you sure you want to delete {selectedComplaints.length} selected complaint{selectedComplaints.length > 1 ? "s" : ""}?
            </p>
            <p className="delete-warning-text">This action cannot be undone.</p>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button className="danger-btn" onClick={handleDeleteComplaint}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Normal Image Preview Modal */}
      {previewImage && (
        <div className="modal-overlay" onClick={() => setPreviewImage(null)}>
          <div
            className="image-preview-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="close-preview" onClick={() => setPreviewImage(null)}>
              &times;
            </button>
            <img src={previewImage} alt="Preview" className="full-image" />
          </div>
        </div>
      )}

      {/* Detection Preview Modal */}
      {previewDetection && (
        <div className="modal-overlay" onClick={() => setPreviewDetection(null)}>
          <div
            className="image-preview-content detection-preview"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="close-preview" onClick={() => setPreviewDetection(null)}>
              &times;
            </button>
            <h3 style={{ color: "#fff", marginBottom: "10px", fontSize: "16px" }}>
              ♻️ Detected Recyclable Items
            </h3>
            <ObjectDetectionViewer
              imageUrl={previewDetection}
              detectedItems={
                complaints.find(
                  (c) =>
                    `${API_BASE_URL}/uploads/${c.image}` === previewDetection
                )?.detectedItems || []
              }
            />
            {/* Items label list */}
            {(() => {
              const comp = complaints.find(
                (c) =>
                  `${API_BASE_URL}/uploads/${c.image}` === previewDetection
              );
              const items = comp?.detectedItems || [];
              if (items.length === 0) return null;
              return (
                <div className="detected-items-list">
                  {items.map((item, i) => (
                    <span key={i} className="detected-item-tag">
                      {item.className} ({Math.round(item.confidence * 100)}%)
                    </span>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplaintControl;
